import React, { useEffect, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Select,
  MenuItem,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";
import { ImFilePdf, ImImage, ImFileWord, ImFileExcel } from "react-icons/im";
import axios from "axios";
import { FadeLoader } from "react-spinners";
import { API_BASE_URL } from "../../config/api";

const CATEGORY_ID = 10; // Javnost rada

const JavnostRada = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  const [selectedYear, setSelectedYear] = useState("Sve");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        let allPosts = [];
        let totalPages = 1;
        let page = 1;

        while (page <= totalPages) {
          const response = await axios.get(
            `${API_BASE_URL}/posts&categories=${CATEGORY_ID}&_embed&_fields=id,title,date,acf&acf_format=standard&per_page=${recordsPerPage}&page=${page}`
          );

          allPosts = allPosts.concat(response.data);
          totalPages = Number(response.headers["x-wp-totalpages"] || 1);
          page++;
        }

        // najnoviji prvi
        allPosts.sort((a, b) => new Date(b.date) - new Date(a.date));

        setData(allPosts);
        setFilter(allPosts);
      } catch (error) {
        console.error("Greška prilikom dohvata:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const availableYears = useMemo(() => {
    const years = data
      .map((post) => (post?.date ? new Date(post.date).getFullYear() : null))
      .filter(Boolean)
      .map(String);

    return [...new Set(years)].sort((a, b) => Number(b) - Number(a));
  }, [data]);

  const applyFilters = (baseData, year, query) => {
    let out = baseData;

    if (year !== "Sve") {
      out = out.filter((post) => {
        const y = post?.date ? String(new Date(post.date).getFullYear()) : "";
        return y === year;
      });
    }

    const q = (query ?? "").toLowerCase().trim();
    if (q) {
      out = out.filter((post) =>
        (post?.title?.rendered ?? "").toLowerCase().includes(q)
      );
    }

    return out;
  };

  const handleSearchChange = (val) => {
    setSearchQuery(val);
    setFilter(applyFilters(data, selectedYear, val));
    setCurrentPage(1);
  };

  const handleYearFilterChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    setFilter(applyFilters(data, year, searchQuery));
    setCurrentPage(1);
  };

  const nPages = Math.ceil(filter.length / recordsPerPage);

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filter.slice(indexOfFirstRecord, indexOfLastRecord);

  return (
    <div className="container drugaSekcija dekanat">
      <div className="container dekanat">
        <div className="container drugaSekcija">
          <h1>Javnost rada</h1>

          {loading ? (
            <div
              className="text-center"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "70vh",
              }}
            >
              <FadeLoader color="#123abc" loading={loading} />
            </div>
          ) : (
            <div className="d-flex bd-highlight">
              <div className="p-2 flex-fill bd-highlight">
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  <TextField
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Traži dokument"
                  />

                  <Select
                    value={selectedYear}
                    onChange={handleYearFilterChange}
                    displayEmpty
                  >
                    <MenuItem value="Sve">Sve godine</MenuItem>
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </div>

                <TableContainer component={Paper} style={{ marginTop: 12 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Naziv dokumenta</TableCell>
                        <TableCell>Datum objave</TableCell>
                        <TableCell>Preuzmi</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {currentRecords.map((row, index) => (
                        <TableRow key={row.id}>
                          <TableCell>
                            {indexOfFirstRecord + index + 1}
                          </TableCell>

                          <TableCell>{row?.title?.rendered || "—"}</TableCell>

                          <TableCell>
                            {row?.date
                              ? new Date(row.date).toLocaleDateString()
                              : "—"}
                          </TableCell>

                          <TableCell>
                            {row?.acf?.dokumenti?.length ? (
                              row.acf.dokumenti.map((el) => {
                                const url = el?.dokument?.url;
                                const subtype = el?.dokument?.subtype;

                                if (!url) return null;

                                let Icon = null;
                                if (subtype === "pdf") Icon = ImFilePdf;
                                else if (["jpeg", "jpg", "png"].includes(subtype))
                                  Icon = ImImage;
                                else if (["doc", "docx"].includes(subtype))
                                  Icon = ImFileWord;
                                else if (
                                  subtype ===
                                    "vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                                  subtype === "vnd.ms-excel" ||
                                  subtype === "xls" ||
                                  subtype === "xlsx"
                                )
                                  Icon = ImFileExcel;

                                return (
                                  <a
                                    key={el?.naziv_dokumenta || url}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginRight: 8 }}
                                  >
                                    {Icon ? <Icon size={22} /> : "Dokument"}
                                  </a>
                                );
                              })
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {nPages > 1 && (
                  <Pagination
                    count={nPages}
                    page={currentPage}
                    onChange={(_, value) => setCurrentPage(value)}
                    style={{ marginTop: 20 }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JavnostRada;
